import React, { useEffect, useState } from 'react';
import { getSubcategoriesWithCategoryName, deleteSubcategory } from '@/lib/categories';
import type { SubcategoryWithCategoryName } from '@/lib/categories';
import type { Subcategory } from '@/types/category';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { Edit, Trash2 } from 'lucide-react'; // Removido PlusCircle se não for mais usado aqui

interface SubcategoriesListProps {
    onEditSubcategory: (subcategory: Subcategory) => void;
    // onAddSubcategory: () => void; // Prop não é mais usada para um botão aqui, pode ser removida se não tiver outro uso.
    refreshKey: number;
}

export function SubcategoriesList({ onEditSubcategory, /* onAddSubcategory, */ refreshKey }: SubcategoriesListProps) {
    const [subcategories, setSubcategories] = useState<SubcategoryWithCategoryName[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSubcategoriesData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSubcategoriesWithCategoryName();
            setSubcategories(data);
        } catch (err) {
            setError('Falha ao carregar subcategorias.');
            console.error(err);
            toast({ title: 'Erro', description: 'Não foi possível carregar as subcategorias.', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubcategoriesData();
    }, [refreshKey]);

    const handleDeleteSubcategory = async (id: string) => {
        const result = await deleteSubcategory(id);
        if (result.error) {
            toast({ title: 'Erro', description: `Falha ao excluir subcategoria: ${result.error}`, variant: 'destructive' });
        } else {
            toast({ title: 'Sucesso', description: 'Subcategoria marcada como excluída.', variant: 'default' });
            fetchSubcategoriesData();
        }
    };

    if (loading) {
        return <div>Carregando subcategorias...</div>;
    }

    if (error) {
        return <div className="text-red-600">{error}</div>;
    }

    return (
        <div className="space-y-4">
            {/* Botão "Nova Subcategoria" foi removido daqui */}
            {subcategories.length === 0 ? (
                 <p className="text-center text-gray-500">Nenhuma subcategoria encontrada.</p>
                 // Você poderia adicionar um botão aqui para adicionar a primeira se a lista estiver vazia E onAddSubcategory for fornecido
                 // Ex: <Button onClick={onAddSubcategory} size="sm" variant="outline">
                 //         <PlusCircle className="mr-2 h-4 w-4" /> Adicionar Primeira Subcategoria
                 //     </Button>
            ) : (
                <div className="border rounded-lg">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nome Subcategoria</TableHead>
                                <TableHead>Categoria Pai</TableHead>
                                <TableHead>Descrição</TableHead>
                                <TableHead>Ordem</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {subcategories.map((sub) => (
                                <TableRow key={sub.id}>
                                    <TableCell className="font-medium">{sub.name}</TableCell>
                                    <TableCell>{sub.categoryName || 'N/A'}</TableCell>
                                    <TableCell>{sub.description || '-'}</TableCell>
                                    <TableCell>{sub.displayOrder}</TableCell>
                                   <TableCell>
                                        <Badge variant={sub.status === 'active' ? 'secondary' : 'outline'}>
                                            {sub.status === 'active' ? 'Ativa' : 'Inativa'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button variant="ghost" size="icon" onClick={() => onEditSubcategory(sub as Subcategory)} title="Editar Subcategoria">
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" title="Excluir Subcategoria">
                                                     <Trash2 className="h-4 w-4" />
                                                 </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Tem certeza que deseja excluir a subcategoria "{sub.name}"?
                                                        Esta ação pode ser reversível (soft delete).
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => handleDeleteSubcategory(sub.id)} className="bg-red-600 hover:bg-red-700">
                                                        Excluir
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
